(() => {
  const finePointerQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
  const OPEN_DELAY = 70;
  const CLOSE_DELAY = 160;
  const MENU_TRANSITION_MS = 340;

  const closeItem = (item) => {
    if (!item) return;
    const trigger = item.querySelector('[data-mega-trigger]');
    const dropdown = item.querySelector('[data-mega-dropdown]');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    if (dropdown) {
      dropdown.classList.remove('is-visible');
      window.setTimeout(() => {
        if (!item.classList.contains('is-open')) {
          dropdown.hidden = true;
        }
      }, MENU_TRANSITION_MS);
    }
    item.classList.remove('is-open');
  };

  const closeOthers = (nav, keepItem) => {
    nav.querySelectorAll('[data-mega-item].is-open').forEach((item) => {
      if (item !== keepItem) closeItem(item);
    });
  };

  const openItem = (nav, item) => {
    if (!item) return;
    const trigger = item.querySelector('[data-mega-trigger]');
    const dropdown = item.querySelector('[data-mega-dropdown]');
    if (!trigger || !dropdown) return;

    closeOthers(nav, item);
    item.classList.add('is-open');
    trigger.setAttribute('aria-expanded', 'true');
    dropdown.hidden = false;
    window.requestAnimationFrame(() => {
      dropdown.classList.add('is-visible');
    });
  };

  const activatePanel = (item, panelId) => {
    if (!item || !panelId) return;

    item.querySelectorAll('[data-hpp-panel-trigger]').forEach((button) => {
      const isActive = button.getAttribute('data-panel-id') === panelId;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-expanded', isActive ? 'true' : 'false');
    });

    item.querySelectorAll('[data-hpp-panel]').forEach((panel) => {
      const isActive = panel.id === panelId;
      panel.classList.toggle('is-active', isActive);
      panel.hidden = !isActive;
    });
  };

  const initMegaNav = (nav) => {
    let openTimer = null;
    let closeTimer = null;

    const scheduleOpen = (item) => {
      window.clearTimeout(openTimer);
      window.clearTimeout(closeTimer);
      openTimer = window.setTimeout(() => openItem(nav, item), OPEN_DELAY);
    };

    const scheduleClose = () => {
      window.clearTimeout(openTimer);
      window.clearTimeout(closeTimer);
      closeTimer = window.setTimeout(() => {
        nav.querySelectorAll('[data-mega-item]').forEach(closeItem);
      }, CLOSE_DELAY);
    };

    nav.addEventListener('mouseenter', () => {
      window.clearTimeout(closeTimer);
    }, true);

    nav.addEventListener('mouseleave', () => {
      if (finePointerQuery.matches) scheduleClose();
    }, true);

    nav.addEventListener('mouseover', (event) => {
      if (!finePointerQuery.matches) return;

      const sidebarButton = event.target.closest('[data-hpp-panel-trigger]');
      if (sidebarButton && nav.contains(sidebarButton)) {
        const item = sidebarButton.closest('[data-mega-item]');
        activatePanel(item, sidebarButton.getAttribute('data-panel-id'));
        return;
      }

      const item = event.target.closest('[data-mega-item]');
      if (item && nav.contains(item) && item.querySelector('[data-mega-trigger]')) {
        scheduleOpen(item);
      }
    });

    nav.addEventListener('focusin', (event) => {
      const item = event.target.closest('[data-mega-item]');
      if (!item || !nav.contains(item)) return;

      if (item.querySelector('[data-mega-trigger]')) {
        openItem(nav, item);
      }

      const sidebarButton = event.target.closest('[data-hpp-panel-trigger]');
      if (sidebarButton) {
        activatePanel(item, sidebarButton.getAttribute('data-panel-id'));
      }
    });

    nav.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-mega-trigger]');
      if (trigger && nav.contains(trigger)) {
        event.preventDefault();
        const item = trigger.closest('[data-mega-item]');
        if (!item) return;

        if (item.classList.contains('is-open')) {
          closeItem(item);
        } else {
          openItem(nav, item);
        }
        return;
      }

      const sidebarButton = event.target.closest('[data-hpp-panel-trigger]');
      if (sidebarButton && nav.contains(sidebarButton)) {
        const item = sidebarButton.closest('[data-mega-item]');
        activatePanel(item, sidebarButton.getAttribute('data-panel-id'));
      }
    });

    document.addEventListener('click', (event) => {
      if (!nav.contains(event.target)) {
        nav.querySelectorAll('[data-mega-item]').forEach(closeItem);
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        nav.querySelectorAll('[data-mega-item]').forEach(closeItem);
      }
    });
  };

  const initSearchBox = (searchBox) => {
    const predictive = searchBox.querySelector('predictive-search');
    const input = searchBox.querySelector('.search__input');
    const defaultPanel = searchBox.querySelector('[data-hpp-search-default]');
    if (!predictive || !input || !defaultPanel) return;

    let closeTimer = null;

    const openDefault = () => {
      window.clearTimeout(closeTimer);
      if (input.value.trim().length || predictive.hasAttribute('open')) return;
      defaultPanel.hidden = false;
      searchBox.classList.add('is-default-open');
    };

    const closeDefault = () => {
      searchBox.classList.remove('is-default-open');
      defaultPanel.hidden = true;
    };

    const scheduleClose = () => {
      window.clearTimeout(closeTimer);
      closeTimer = window.setTimeout(() => {
        if (!searchBox.contains(document.activeElement) && !predictive.hasAttribute('open')) {
          closeDefault();
        }
      }, 120);
    };

    input.addEventListener('focus', openDefault);
    input.addEventListener('click', openDefault);
    input.addEventListener('input', () => {
      if (input.value.trim().length) {
        closeDefault();
      } else {
        openDefault();
      }
    });

    predictive.addEventListener('focusout', scheduleClose);

    const observer = new MutationObserver(() => {
      if (predictive.hasAttribute('open')) {
        closeDefault();
      } else if (document.activeElement === input && !input.value.trim().length) {
        openDefault();
      }
    });

    observer.observe(predictive, {
      attributes: true,
      attributeFilter: ['open'],
    });

    document.addEventListener('click', (event) => {
      if (!searchBox.contains(event.target)) {
        closeDefault();
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        closeDefault();
      }
    });
  };

  const initMobileNav = (container) => {
    const details = container.closest('details');
    const drawer = container.querySelector('.hpp-mobile-nav');
    const closeButton = container.querySelector('[data-close-mobile-nav]');
    const openSearchButton = container.querySelector('[data-open-mobile-search]');
    const railButtons = container.querySelectorAll('[data-mobile-rail-trigger]');
    const panels = container.querySelectorAll('[data-mobile-showcase-panel]');
    const summary = details ? details.querySelector('summary') : null;
    if (!details || !drawer || !railButtons.length || !panels.length) return;
    const defaultRailButton = railButtons[0];

    const activatePanel = (panelId) => {
      railButtons.forEach((button) => {
        const isActive = button.getAttribute('data-mobile-panel-id') === panelId;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-expanded', isActive ? 'true' : 'false');
      });

      panels.forEach((panel) => {
        const isActive = panel.id === panelId;
        panel.classList.toggle('is-active', isActive);
        panel.hidden = !isActive;
      });
    };

    const closeDrawer = () => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
      document.body.classList.remove('overflow-hidden-mobile');
      if (summary) summary.setAttribute('aria-expanded', 'false');
    };

    railButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        details.setAttribute('open', '');
        details.classList.add('menu-opening');
        activatePanel(button.getAttribute('data-mobile-panel-id'));
      });
    });

    if (closeButton) {
      closeButton.addEventListener('click', closeDrawer);
    }

    if (openSearchButton) {
      openSearchButton.addEventListener('click', () => {
        closeDrawer();
        const mobileSearchTrigger = document.querySelector('.hpp-header__mobile-right .header__search summary');
        if (mobileSearchTrigger) {
          window.setTimeout(() => mobileSearchTrigger.click(), 20);
        }
      });
    }

    drawer.addEventListener('click', (event) => {
      if (event.target === drawer) {
        closeDrawer();
      }
    });

    details.addEventListener('toggle', () => {
      const isOpen = details.hasAttribute('open');
      document.body.classList.toggle('overflow-hidden-mobile', isOpen);
      if (summary) summary.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      if (isOpen && defaultRailButton) {
        activatePanel(defaultRailButton.getAttribute('data-mobile-panel-id'));
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && details.hasAttribute('open')) {
        closeDrawer();
      }
    });
  };

  document.querySelectorAll('[data-hpp-mega-nav]').forEach(initMegaNav);
  document.querySelectorAll('.hpp-search').forEach(initSearchBox);
  document.querySelectorAll('[data-hpp-mobile-nav-container]').forEach(initMobileNav);
})();
